/**
 * Manage PRS Dialog - Improved UX for uploading Base and CIS PRS
 * Shows separate buttons for each type with status indicators
 */

import React, {useState, useCallback, useEffect} from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  LinearProgress,
  Paper,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import {apiService} from '../services/api';

interface ManagePRSDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  trialId: string;
}

interface PRSStatus {
  exists: boolean;
  version?: string;
  uploadedAt?: string;
  filename?: string;
}

export const ManagePRSDialog: React.FC<ManagePRSDialogProps> = ({
  open,
  onClose,
  onSuccess,
  trialId,
}) => {
  const [basePRSStatus, setBasePRSStatus] = useState<PRSStatus>({exists: false});
  const [cisPRSStatus, setCisPRSStatus] = useState<PRSStatus>({exists: false});
  const [loading, setLoading] = useState(false);
  const [uploadingBase, setUploadingBase] = useState(false);
  const [uploadingCIS, setUploadingCIS] = useState(false);
  const [baseProgress, setBaseProgress] = useState(0);
  const [cisProgress, setCisProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [uploadWarning, setUploadWarning] = useState<string | null>(null);

  // Check existing PRS versions when dialog opens
  const checkExistingPRS = useCallback(async () => {
    if (!trialId) return;

    try {
      setLoading(true);
      setError(null);
      const versions = await apiService.listPRSVersions(trialId, 'app');

      // Find base PRS (is_base: true)
      const baseVersion = Object.entries(versions).find(([_, v]: any) => v.is_base);
      if (baseVersion) {
        setBasePRSStatus({
          exists: true,
          version: baseVersion[0],
          uploadedAt: baseVersion[1].uploaded_at,
          filename: baseVersion[1].filename,
        });
      } else {
        setBasePRSStatus({exists: false});
      }

      // Find CIS PRS (is_base: false) - get the most recent one
      const cisVersions = Object.entries(versions)
        .filter(([_, v]: any) => !v.is_base)
        .sort((a: any, b: any) => {
          const dateA = new Date(a[1].uploaded_at || 0).getTime();
          const dateB = new Date(b[1].uploaded_at || 0).getTime();
          return dateB - dateA;
        });

      if (cisVersions.length > 0) {
        setCisPRSStatus({
          exists: true,
          version: cisVersions[0][0],
          uploadedAt: cisVersions[0][1].uploaded_at,
          filename: cisVersions[0][1].filename,
        });
      } else {
        setCisPRSStatus({exists: false});
      }
    } catch (err: any) {
      // If trial doesn't exist yet, that's fine - no PRS uploaded yet
      setBasePRSStatus({exists: false});
      setCisPRSStatus({exists: false});
    } finally {
      setLoading(false);
    }
  }, [trialId]);

  useEffect(() => {
    if (open) {
      checkExistingPRS();
      setError(null);
      setSuccessMessage(null);
      setUploadWarning(null);
    }
  }, [open, checkExistingPRS]);

  const handleFileSelect = async (isBase: boolean) => {
    // Create a file input dynamically
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.xlsx,.xls';
    
    input.onchange = async (e: any) => {
      const file = e.target?.files?.[0];
      if (!file) return;

      // Validate file type
      const validExtensions = ['.xlsx', '.xls'];
      const isValidType = validExtensions.some(ext =>
        file.name.toLowerCase().endsWith(ext)
      );

      if (!isValidType) {
        setError('Please select a valid Excel file (.xlsx or .xls)');
        return;
      }

      await handleUpload(file, isBase);
    };

    input.click();
  };

  const handleUpload = async (file: File, isBase: boolean) => {
    const setUploading = isBase ? setUploadingBase : setUploadingCIS;
    const setProgress = isBase ? setBaseProgress : setCisProgress;
    const prsType = isBase ? 'Base' : 'CIS';

    try {
      setUploading(true);
      setError(null);
      setSuccessMessage(null);
      setUploadWarning(null);
      setProgress(0);

      // Simulate progress
      const progressInterval = setInterval(() => {
        setProgress(prev => Math.min(prev + 10, 90));
      }, 300);

      console.log(`Uploading ${prsType} PRS: trial_id=${trialId}, is_base=${isBase}`);

      // Upload PRS
      const response = await apiService.uploadPRS(trialId, file, 'app', isBase);

      clearInterval(progressInterval);

      // Check if API returned an error
      if (response.success === false) {
        setProgress(0);
        setError(response.error || 'Upload failed. Please try again.');
        return;
      }

      setProgress(100);

      // Show success message
      const action = (isBase && basePRSStatus.exists) || (!isBase && cisPRSStatus.exists) 
        ? 'replaced' 
        : 'uploaded';
      setSuccessMessage(`${prsType} PRS ${action} successfully!`);
      if (response.warning) {
        setUploadWarning(response.warning);
      }

      // Refresh status
      setTimeout(async () => {
        await checkExistingPRS();
        setProgress(0);
        onSuccess();
      }, 1000);
    } catch (err: any) {
      console.error(`Error uploading ${prsType} PRS:`, err);
      
      // Provide user-friendly error messages
      let errorMessage = 'An invalid file was provided, please select a valid Excel file and try again';
      
      // Check for specific error types
      if (err.response?.status === 400) {
        errorMessage = 'An invalid file was provided, please select a valid Excel file and try again';
      } else if (err.response?.status === 500) {
        errorMessage = 'An invalid file was provided, please select a valid Excel file and try again';
      } else if (err.response?.status === 413) {
        errorMessage = 'The file is too large. Please ensure the Excel file is under the size limit';
      } else if (err.message?.toLowerCase().includes('network')) {
        errorMessage = 'Network error. Please check your connection and try again';
      } else if (err.message?.toLowerCase().includes('timeout')) {
        errorMessage = 'Upload timed out. Please try again';
      }
      
      setError(errorMessage);
      setProgress(0);
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    if (!uploadingBase && !uploadingCIS) {
      setError(null);
      setSuccessMessage(null);
      setUploadWarning(null);
      setBaseProgress(0);
      setCisProgress(0);
      onClose();
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const isAnyUploading = uploadingBase || uploadingCIS;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box>
          <Typography variant="h6" component="div">
            Manage PRS
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Trial ID: {trialId}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{mt: 2}}>
          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{mb: 3}} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Success Alert */}
          {successMessage && (
            <Alert severity="success" sx={{mb: 3}} onClose={() => setSuccessMessage(null)}>
              {successMessage}
            </Alert>
          )}

          {/* Warning from upload response */}
          {uploadWarning && (
            <Alert severity="warning" sx={{mb: 3}} onClose={() => setUploadWarning(null)}>
              {uploadWarning}
            </Alert>
          )}

          {/* Loading State */}
          {loading && (
            <Box sx={{mb: 3, textAlign: 'center'}}>
              <LinearProgress />
              <Typography variant="caption" color="text.secondary" sx={{mt: 1}}>
                Checking existing PRS files...
              </Typography>
            </Box>
          )}

          {/* PRS Upload Cards */}
          {!loading && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {xs: '1fr', sm: '1fr 1fr'},
                gap: 3,
              }}
            >
              {/* Base PRS Card */}
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Base PRS
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Initial (base) project requirements specification
                  </Typography>
                </Box>

                {/* Status Indicator */}
                {basePRSStatus.exists && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      p: 1.5,
                      bgcolor: (theme) => 
                        theme.palette.mode === 'dark' 
                          ? 'rgba(46, 125, 50, 0.2)' 
                          : 'success.light',
                      border: '1px solid',
                      borderColor: 'success.main',
                      borderRadius: 1,
                    }}
                  >
                    <CheckIcon 
                      sx={{
                        color: (theme) =>
                          theme.palette.mode === 'dark'
                            ? 'success.main'
                            : 'success.dark',
                      }}
                    />
                    <Box>
                      <Typography 
                        variant="body2" 
                        fontWeight="medium"
                        sx={{
                          color: (theme) =>
                            theme.palette.mode === 'dark'
                              ? 'success.main'
                              : 'success.dark',
                        }}
                      >
                        PRS Uploaded
                      </Typography>
                      <Typography 
                        variant="caption"
                        sx={{
                          color: (theme) =>
                            theme.palette.mode === 'dark'
                              ? 'success.main'
                              : 'success.dark',
                          opacity: 0.8,
                        }}
                      >
                        {basePRSStatus.filename}
                        {basePRSStatus.version != null && ` · Version ${basePRSStatus.version}`}
                        {` · ${formatDate(basePRSStatus.uploadedAt)}`}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {!basePRSStatus.exists && (
                  <Box
                    sx={{
                      p: 1.5,
                      bgcolor: 'action.hover',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      textAlign: 'center',
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      No base PRS uploaded yet
                    </Typography>
                  </Box>
                )}

                {/* Upload Progress */}
                {uploadingBase && (
                  <Box>
                    <LinearProgress variant="determinate" value={baseProgress} />
                    <Typography variant="caption" color="text.secondary" sx={{mt: 0.5}}>
                      Uploading... {baseProgress}%
                    </Typography>
                  </Box>
                )}

                {/* Upload Button */}
                <Button
                  variant={basePRSStatus.exists ? 'outlined' : 'contained'}
                  startIcon={<UploadIcon />}
                  onClick={() => handleFileSelect(true)}
                  disabled={isAnyUploading}
                  fullWidth
                >
                  {basePRSStatus.exists ? 'Replace Base PRS' : 'Upload Base PRS'}
                </Button>
              </Paper>

              {/* CIS PRS Card */}
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                <Box>
                  <Typography variant="h6" gutterBottom>
                    CIS PRS
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Updated project requirements specification
                  </Typography>
                </Box>

                {/* Status Indicator */}
                {cisPRSStatus.exists && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      p: 1.5,
                      bgcolor: (theme) => 
                        theme.palette.mode === 'dark' 
                          ? 'rgba(46, 125, 50, 0.2)' 
                          : 'success.light',
                      border: '1px solid',
                      borderColor: 'success.main',
                      borderRadius: 1,
                    }}
                  >
                    <CheckIcon 
                      sx={{
                        color: (theme) =>
                          theme.palette.mode === 'dark'
                            ? 'success.main'
                            : 'success.dark',
                      }}
                    />
                    <Box>
                      <Typography 
                        variant="body2" 
                        fontWeight="medium"
                        sx={{
                          color: (theme) =>
                            theme.palette.mode === 'dark'
                              ? 'success.main'
                              : 'success.dark',
                        }}
                      >
                        PRS Uploaded
                      </Typography>
                      <Typography 
                        variant="caption"
                        sx={{
                          color: (theme) =>
                            theme.palette.mode === 'dark'
                              ? 'success.main'
                              : 'success.dark',
                          opacity: 0.8,
                        }}
                      >
                        {cisPRSStatus.filename}
                        {cisPRSStatus.version != null && ` · Version ${cisPRSStatus.version}`}
                        {` · ${formatDate(cisPRSStatus.uploadedAt)}`}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {!cisPRSStatus.exists && (
                  <Box
                    sx={{
                      p: 1.5,
                      bgcolor: 'action.hover',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      textAlign: 'center',
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      No CIS PRS uploaded yet
                    </Typography>
                  </Box>
                )}

                {/* Upload Progress */}
                {uploadingCIS && (
                  <Box>
                    <LinearProgress variant="determinate" value={cisProgress} />
                    <Typography variant="caption" color="text.secondary" sx={{mt: 0.5}}>
                      Uploading... {cisProgress}%
                    </Typography>
                  </Box>
                )}

                {/* Upload Button */}
                <Button
                  variant={cisPRSStatus.exists ? 'outlined' : 'contained'}
                  startIcon={<UploadIcon />}
                  onClick={() => handleFileSelect(false)}
                  disabled={isAnyUploading}
                  fullWidth
                >
                  {cisPRSStatus.exists ? 'Replace CIS PRS' : 'Upload CIS PRS'}
                </Button>
              </Paper>
            </Box>
          )}

          {/* Helper Text */}
          <Box 
            sx={{
              mt: 3, 
              p: 2, 
              bgcolor: 'action.hover',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              <strong>Tip:</strong> The Base PRS is your original protocol specification. 
              The CIS PRS is the updated version with changes in scope, ensure to document the new version under version history sheet.
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isAnyUploading}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
